import Constants from '@/Constants';
import useCategories from '@/hooks/useCategories';
import Category from '@/models/Category';
import { ApplicationState } from '@/store';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import './styles.scss';

interface Props {
    categoriesForm: Category[];
    familyId?: number;
    handleOnChange: (fieldName: string, value: Category[] | undefined, index?: number) => void;
    isColumnFormat?: boolean;
}

const SelectCategoryForm: React.FC<Props> = ({ categoriesForm, familyId, handleOnChange, isColumnFormat = false }: Props) => {
    const strings = useSelector((state: ApplicationState) => state.locale.data);

    const [firstCategoryId, setFirstCategoryId] = useState<string>();
    const [secondCategoryId, setSecondCategoryId] = useState<string>();
    const [showSecondCategory, setShowSecondCategory] = useState<boolean>(false);

    const { categories } = useCategories({ familyId, limit: 0 });

    const categoriesClassName: string = useMemo(() => (isColumnFormat ? 'categories-column' : 'categories'), [isColumnFormat]);

    const selectFirstCategory = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const categoryId = event.target.value;

        if (categoryId === strings.SELECT_CATEGORY) {
            setFirstCategoryId(undefined);
            setSecondCategoryId(undefined);
            setShowSecondCategory(false);
            return;
        }

        setFirstCategoryId(categoryId);
    };

    const selectSecondCategory = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const categoryId = event.target.value;

        if (categoryId === strings.SELECT_CATEGORY) {
            setSecondCategoryId(undefined);
            return;
        }

        setSecondCategoryId(categoryId);
    };

    const onChange = (): void => {
        const firstCategory = categories.find(({ id }) => firstCategoryId === String(id));
        const secondCategory = categories.find(({ id }) => secondCategoryId === String(id));
        let updatedCategories: Category[] | undefined = undefined;

        if (firstCategory) {
            updatedCategories = [firstCategory];
        }

        if (updatedCategories && secondCategory) {
            updatedCategories = [...updatedCategories, secondCategory];
        }

        handleOnChange('categories', updatedCategories);
    };

    const loadCategories = () => {
        if (!firstCategoryId && categoriesForm) {
            const [firstCategory, secondCategory] = categoriesForm;

            if (firstCategory?.id) {
                setFirstCategoryId(String(firstCategory.id));
            }

            if (secondCategory?.id) {
                setSecondCategoryId(String(secondCategory.id));
                setShowSecondCategory(true);
            }
        }
    };

    const deleteSecondCategory = () => {
        setShowSecondCategory(false);
        setSecondCategoryId(undefined);
    };

    useEffect(loadCategories, [categoriesForm]);

    useEffect(() => {
        if (firstCategoryId) {
            setFirstCategoryId(undefined);
            setSecondCategoryId(undefined);
            setShowSecondCategory(false);
        }
    }, [familyId]);

    useEffect(onChange, [firstCategoryId, secondCategoryId]);

    return (
        <div className="select-category-container">
            <h1 className="warning">{strings.CATEGORY}</h1>
            <div className={categoriesClassName}>
                <select className="form-control" onChange={selectFirstCategory} value={firstCategoryId || ''}>
                    <option value={strings.SELECT_CATEGORY}>{strings.SELECT_CATEGORY}</option>
                    {categories
                        .filter((category: Category) => String(category.id) !== secondCategoryId)
                        .map((category: Category) => (
                            <option key={category.id} value={category.id}>
                                {category.name}
                            </option>
                        ))}
                </select>

                {showSecondCategory ? (
                    <div className={`${categoriesClassName}__second`}>
                        <select className="form-control" onChange={selectSecondCategory} value={secondCategoryId || ''}>
                            <option value={strings.SELECT_CATEGORY}>{strings.SELECT_CATEGORY}</option>
                            {categories
                                .filter((category: Category) => String(category.id) !== firstCategoryId)
                                .map((category: Category) => (
                                    <option key={category.id} value={category.id}>
                                        {category.name}
                                    </option>
                                ))}
                        </select>

                        <button className="btn btn-link" onClick={deleteSecondCategory}>
                            <i className="material-icons-outlined align-middle">{Constants.icons.DELETE}</i>
                            {strings.REMOVE}
                        </button>
                    </div>
                ) : (
                    <>
                        <div className={`d-flex ${isColumnFormat ? 'justify-content-end' : ''}`}>
                            {!!firstCategoryId && (
                                <button className="btn btn-link" onClick={() => setShowSecondCategory(true)}>
                                    <span>{strings.ADD_CATEGORY}</span>
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default memo(SelectCategoryForm);
